// Community patches (songs) browser. Three things stacked:
//   1. "Recently played" rail at the top — patches with at least one
//      play in the last 24h, ordered by most-recent play.
//   2. The public patches feed (most-recent first), with fork + play
//      buttons inline. Click the row to expand a comments thread.
//   3. The expanded thread reads patch_comments via direct Supabase
//      RLS, lets the signed-in user post + soft-delete their own.
//
// Forking pulls the new patch's code into a fresh local Song so the
// user can immediately start editing. Play hits the anonymous
// counter; we fire-and-forget so the play feedback is instant.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  forkPatchToSong,
  getParent,
  listCommunityPatches,
  listForks,
  listRecentlyPlayed,
  recordPlayRequest,
  type CommunityPatch,
  type CommunityPatchSort,
  type LineagePatch,
  type RecentlyPlayedItem,
} from '../../cloud';
import { isCloudConfigured } from '../../cloud/env';
import { buildShareUrl } from '../../share/route';
import { useSession } from '../../auth/useSession';
import { CommentsThread } from './CommentsThread';

const SEARCH_DEBOUNCE_MS = 350;

export type CommunityPatchesProps = {
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
  /** Called after a fork lands. Caller seeds a new local Song with
   *  the forked code and opens it in the editor. */
  onForked: (params: { title: string; code: string; cloudPatchId: string }) => void;
  /** Called when a user clicks Play on a community patch — caller
   *  loads it into the editor and triggers Strudel.evaluate. */
  onPlay: (params: { title: string; code: string; cloudPatchId: string }) => void;
};

type LoadState = 'idle' | 'loading' | 'loadingMore' | 'error';

export function CommunityPatches({ onStatus, onForked, onPlay }: CommunityPatchesProps) {
  const { session } = useSession();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState<CommunityPatchSort>('new');
  const [items, setItems] = useState<CommunityPatch[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentlyPlayedItem[]>([]);
  const [forkingId, setForkingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Bumped on every query change. loadMore captures the epoch at click
  // time and drops its response if the user changed the query before
  // it landed (mirrors the pattern in CommunityLibrary.tsx).
  const queryEpoch = useRef(0);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  // Initial + query/sort-change fetch.
  useEffect(() => {
    queryEpoch.current += 1;
    const epoch = queryEpoch.current;
    setLoadState('loading');
    setErrorMsg(null);
    listCommunityPatches({ q: debouncedQuery || undefined, sort })
      .then((res) => {
        if (queryEpoch.current !== epoch) return;
        setItems(res.items);
        setNextCursor(res.nextCursor);
        setLoadState('idle');
      })
      .catch((e) => {
        if (queryEpoch.current !== epoch) return;
        setErrorMsg(e instanceof Error ? e.message : String(e));
        setItems([]);
        setNextCursor(null);
        setLoadState('error');
      });
  }, [debouncedQuery, sort]);

  // Recently played — separate query, runs once on mount + after a play
  // is recorded so the strip stays warm.
  const refreshRecent = useCallback(() => {
    listRecentlyPlayed()
      .then(setRecent)
      .catch(() => {
        // Non-critical; the feed is decorative.
      });
  }, []);
  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadState === 'loadingMore') return;
    const epoch = queryEpoch.current;
    setLoadState('loadingMore');
    try {
      const res = await listCommunityPatches({
        q: debouncedQuery || undefined,
        cursor: nextCursor,
        sort,
      });
      // Drop the response if the query changed under us — the
      // first-page fetch is the source of truth now.
      if (queryEpoch.current !== epoch) return;
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
      setLoadState('idle');
    } catch (e) {
      if (queryEpoch.current !== epoch) return;
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setLoadState('error');
    }
  }, [nextCursor, loadState, debouncedQuery, sort]);

  const handleFork = useCallback(
    async (entry: CommunityPatch) => {
      if (!session) {
        onStatus('Sign in to fork community songs.', 'warn');
        return;
      }
      setForkingId(entry.id);
      try {
        const fork = await forkPatchToSong(entry.id, {
          title: entry.title,
          authorHandle: entry.authorHandle,
        });
        onForked({
          title: fork.title,
          code: fork.code,
          cloudPatchId: fork.cloudPatchId,
        });
        if (fork.lineageMissing) {
          onStatus(
            `Forked "${entry.title}" (lineage record skipped — fork count won't update).`,
            'warn',
          );
        } else {
          onStatus(`Forked "${entry.title}" — opened in the editor.`, 'ok');
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onStatus('Fork failed: ' + msg, 'err');
      } finally {
        setForkingId(null);
      }
    },
    [session, onForked, onStatus],
  );

  // Accepts any {id,title,code} triple — feed rows and lineage strip
  // entries (which aren't necessarily in `items`) share this path.
  const handlePlay = useCallback(
    (entry: Pick<CommunityPatch, 'id' | 'title' | 'code'>) => {
      onPlay({ title: entry.title, code: entry.code, cloudPatchId: entry.id });
      // Record play, then refresh the recent strip a moment later. We
      // don't block the audio path on either — the play is the primary
      // action and the counter is decorative.
      recordPlayRequest(entry.id).catch(() => {
        // Anonymous endpoint; silent failure is fine.
      });
      window.setTimeout(refreshRecent, 1500);
      // Locally bump the visible count too so the user sees feedback
      // before the next refresh.
      setItems((prev) =>
        prev.map((p) =>
          p.id === entry.id ? { ...p, playsCount: p.playsCount + 1 } : p,
        ),
      );
    },
    [onPlay, refreshRecent],
  );

  if (!isCloudConfigured()) {
    return <CloudUnconfigured />;
  }

  return (
    <div className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0">
      <Search value={query} onChange={setQuery} visible={items.length} />
      <SortChips sort={sort} onChange={setSort} />

      <div
        aria-label="Community songs"
        className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 pt-1 space-y-2 scrollbar-thin"
      >
        {recent.length > 0 && <RecentlyPlayedStrip items={recent} />}

        {loadState === 'loading' && items.length === 0 ? (
          <Loading />
        ) : loadState === 'error' ? (
          <ErrorState message={errorMsg ?? 'Unknown error'} />
        ) : items.length === 0 ? (
          <NoMatches query={debouncedQuery} sort={sort} />
        ) : (
          <>
            {items.map((entry) => (
              <PatchRow
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                forking={forkingId === entry.id}
                onToggleExpanded={() =>
                  setExpandedId((prev) => (prev === entry.id ? null : entry.id))
                }
                onFork={() => void handleFork(entry)}
                onPlay={() => handlePlay(entry)}
                onPlayPatch={handlePlay}
                onCopyStatus={onStatus}
                signedIn={!!session}
              />
            ))}
            {nextCursor && (
              <LoadMoreButton
                disabled={loadState === 'loadingMore'}
                onClick={() => void loadMore()}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────

function Search({
  value,
  onChange,
  visible,
}: {
  value: string;
  onChange: (v: string) => void;
  visible: number;
}) {
  return (
    <div className="px-4 pt-3 pb-2">
      <label className="relative block">
        <span className="sr-only">Search community songs</span>
        <input
          type="text"
          inputMode="search"
          placeholder="search community songs…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-cyan-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[11px] text-zinc-200 placeholder:text-zinc-600 placeholder:tracking-wide pl-3 pr-12 py-1.5 transition-colors font-body"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 tabular-nums tracking-wider">
          {visible}
        </span>
      </label>
    </div>
  );
}

// NEW | TRENDING feed-order toggle — stamped-metal chip pair in the
// hardware-toggle idiom (state changes the fill, cyan like the other
// feed affordances).
function SortChips({
  sort,
  onChange,
}: {
  sort: CommunityPatchSort;
  onChange: (sort: CommunityPatchSort) => void;
}) {
  const chip = (value: CommunityPatchSort, label: string) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={sort === value}
      className={
        'text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-sm border transition-colors ' +
        (sort === value
          ? 'bg-zinc-800 border-cyan-300/60 text-cyan-300'
          : 'bg-zinc-900 border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5 px-4 pb-2">
      {chip('new', 'New')}
      {chip('trending', 'Trending · 7d')}
    </div>
  );
}

function RecentlyPlayedStrip({ items }: { items: RecentlyPlayedItem[] }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-2">
      <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5">
        Recently played · 24h
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it) => (
          <span
            key={it.patchId}
            title={`${it.recentPlays} play${it.recentPlays === 1 ? '' : 's'} by @${it.authorHandle}`}
            className="text-[10px] px-2 py-0.5 rounded-sm bg-zinc-800 text-zinc-300 border border-zinc-700"
          >
            {it.title}{' '}
            <span className="text-zinc-500 tabular-nums">×{it.recentPlays}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function PatchRow({
  entry,
  expanded,
  forking,
  signedIn,
  onToggleExpanded,
  onFork,
  onPlay,
  onPlayPatch,
  onCopyStatus,
}: {
  entry: CommunityPatch;
  expanded: boolean;
  forking: boolean;
  signedIn: boolean;
  onToggleExpanded: () => void;
  onFork: () => void;
  onPlay: () => void;
  onPlayPatch: (patch: Pick<CommunityPatch, 'id' | 'title' | 'code'>) => void;
  onCopyStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
}) {
  return (
    <div
      className={
        'border bg-zinc-900/50 rounded-sm overflow-hidden transition-colors ' +
        (expanded ? 'border-cyan-300/60' : 'border-zinc-800 hover:border-zinc-700')
      }
    >
      <div className="flex items-center gap-2.5 px-2.5 py-2">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex-1 min-w-0 text-left"
          aria-expanded={expanded}
          aria-label={`Toggle details for ${entry.title}`}
        >
          <div className="text-[11px] text-zinc-100 font-body truncate">
            {entry.title}
          </div>
          <div className="text-[9px] text-zinc-500 tracking-wider tabular-nums flex items-center gap-2">
            <span>@{entry.authorHandle}</span>
            <span>·</span>
            <span>▶ {entry.playsCount}</span>
            <span>·</span>
            <span>⑂ {entry.forksCount}</span>
          </div>
        </button>
        <CopyLinkButton url={buildShareUrl(entry.id)} onStatus={onCopyStatus} />
        <button
          type="button"
          onClick={onPlay}
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700"
          title="Load and play this song"
        >
          Play
        </button>
        <button
          type="button"
          onClick={onFork}
          disabled={forking || !signedIn}
          title={signedIn ? 'Fork into your library' : 'Sign in to fork'}
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          {forking ? '…' : 'Fork'}
        </button>
      </div>
      {expanded && (
        <div className="border-t border-zinc-800 p-2.5 bg-zinc-950/40 space-y-2.5">
          <LineageStrip patchId={entry.id} onPlayPatch={onPlayPatch} />
          <CommentsThread patchId={entry.id} />
        </div>
      )}
    </div>
  );
}

// Fork lineage for an expanded row — where this song came from and
// which visible forks it spawned. Fetched lazily on expand; lineage is
// decorative, so a failed read just renders nothing. Clicking either
// direction loads that patch through the same play path as a feed row.
function LineageStrip({
  patchId,
  onPlayPatch,
}: {
  patchId: string;
  onPlayPatch: (patch: Pick<CommunityPatch, 'id' | 'title' | 'code'>) => void;
}) {
  const [parent, setParent] = useState<LineagePatch | null>(null);
  const [forks, setForks] = useState<LineagePatch[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setParent(null);
    setForks([]);
    Promise.all([getParent(patchId), listForks(patchId)])
      .then(([p, f]) => {
        if (cancelled) return;
        setParent(p);
        setForks(f);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [patchId]);

  if (!loaded || (!parent && forks.length === 0)) return null;

  const play = (p: LineagePatch) =>
    onPlayPatch({ id: p.patchId, title: p.title, code: p.code });

  return (
    <div className="space-y-1.5">
      {parent && (
        <div className="text-[10px] text-zinc-500 tracking-wide truncate">
          <span aria-hidden="true">⑂ </span>
          forked from{' '}
          <button
            type="button"
            onClick={() => play(parent)}
            title={`Load and play "${parent.title}"`}
            className="text-zinc-300 hover:text-cyan-300 underline decoration-zinc-700 underline-offset-2"
          >
            {parent.title}
          </button>{' '}
          by @{parent.authorHandle}
        </div>
      )}
      {forks.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 mb-1">
            Forks of this
          </div>
          <div className="flex flex-wrap gap-1">
            {forks.map((f) => (
              <button
                key={f.patchId}
                type="button"
                onClick={() => play(f)}
                title={`Load and play "${f.title}" by @${f.authorHandle}`}
                className="text-[10px] px-2 py-0.5 rounded-sm bg-zinc-800 text-zinc-300 border border-zinc-700 hover:text-cyan-300 hover:border-cyan-300/50"
              >
                {f.title}{' '}
                <span className="text-zinc-500">@{f.authorHandle}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Copy a share link to the clipboard with a brief "copied" state on
// the button itself, plus a status-bar note (private songs are never in
// the public feed, so a listed song's link always resolves for others).
function CopyLinkButton({
  url,
  onStatus,
}: {
  url: string;
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        onStatus('Share link copied.', 'ok');
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => onStatus('Could not copy link.', 'err'));
  }, [url, onStatus]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy a shareable link to this song"
      aria-label="Copy share link"
      className={
        'text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-sm border ' +
        (copied
          ? 'bg-zinc-800 border-cyan-300/60 text-cyan-300'
          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-cyan-300 hover:border-cyan-300/50')
      }
    >
      {copied ? '✓' : '🔗'}
    </button>
  );
}

function Loading() {
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      Loading…
    </div>
  );
}

function NoMatches({
  query,
  sort,
}: {
  query: string;
  sort: CommunityPatchSort;
}) {
  const empty =
    sort === 'trending'
      ? 'No plays in the last 7 days yet'
      : 'No public songs yet';
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      {query ? `No matches for "${query}"` : empty}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center text-[10px] tracking-[0.2em] text-rose-300 py-6 px-3">
      <div className="uppercase mb-1">Couldn't load community feed</div>
      <div className="text-zinc-500 normal-case tracking-normal text-[11px]">
        {message}
      </div>
    </div>
  );
}

function CloudUnconfigured() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        Cloud not configured
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[220px]">
        Set <code className="text-zinc-300">VITE_SUPABASE_URL</code> and friends in{' '}
        <code className="text-zinc-300">.env.local</code> to browse community songs.
      </p>
    </div>
  );
}

function LoadMoreButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full px-3 py-2 mt-2 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-cyan-300 hover:border-cyan-300/50 disabled:opacity-30 transition-colors rounded-sm"
    >
      {disabled ? 'Loading…' : 'Load more'}
    </button>
  );
}
